import {Conversation, Client} from '@twilio/conversations';
import {UninitailizedClientError} from './errors';
import type {ClientEvents} from './types';

/**
 * Service that directly interacts with Twilio Conversation.
 */
export class TwilioApiService {
  private _client?: Client;

  private _clientToken?: string;

  private conversations: Record<string, Conversation> = {};

  get client() {
    if (this._client) {
      return this._client;
    }
    throw new UninitailizedClientError();
  }

  private async getConversationBySid(conversationId: string) {
    if (!this.conversations[conversationId]) {
      this.conversations[conversationId] =
        await this.client.getConversationBySid(conversationId);
    }

    return this.conversations[conversationId];
  }

  async initialize(token: string) {
    if (this._client) {
      if (this._clientToken !== token) {
        await this._client.updateToken(token);
        this._clientToken = token;
      }
    } else {
      // This is deprecated, but it's blocking to make sure the client is fully initialized
      this._client = await Client.create(token);
      this._clientToken = token;
    }
  }

  async shutdown() {
    await this.client.shutdown();
  }

  registerClientEventCallback<T extends keyof ClientEvents>(
    event: T,
    callback: ClientEvents[T],
  ) {
    this.client.onWithReplay(event, callback);
  }

  clearClientEventCallback() {
    this.client.removeAllListeners();
  }

  async getMessages(
    conversationId: string,
    pageSize?: number,
    anchor?: number,
    direction?: 'backwards' | 'forward',
  ) {
    const conversation = await this.getConversationBySid(conversationId);
    const messages = await conversation.getMessages(
      pageSize,
      anchor,
      direction,
    );
    return messages;
  }

  async getParticipant(conversationId: string, identity: string) {
    const conversation = await this.getConversationBySid(conversationId);
    const participant = await conversation.getParticipantByIdentity(identity);
    return participant;
  }

  async getUser(identity: string) {
    const user = await this.client.getUser(identity);
    return user;
  }

  async getConversation(conversationId: string) {
    const conversation = await this.getConversationBySid(conversationId);
    return conversation;
  }

  async leaveConversation(conversationId: string) {
    const conversation = await this.getConversationBySid(conversationId);
    return conversation.leave();
  }

  async sendMessage(conversationId: string, message: string, nonce?: string) {
    const conversation = await this.getConversationBySid(conversationId);
    const index = await conversation.sendMessage(
      message,
      nonce ? {nonce} : undefined,
    );
    return index;
  }

  async sendUserEndedChatMessage(conversationId: string) {
    const conversation = await this.getConversationBySid(conversationId);
    const messages = (await conversation.getMessages())?.items;
    if (messages?.length) {
      await conversation.sendMessage(
        'Customer has disconnected from the chat',
        {
          userEndedChat: true,
          notification: true,
        },
      );
    }
  }

  async sendFile(conversationId: string, file: File, nonce?: string) {
    const formData = new FormData();
    formData.append('file', file);
    const conversation = await this.getConversationBySid(conversationId);
    const index = conversation.sendMessage(
      formData,
      nonce ? {nonce} : undefined,
    );
    return index;
  }

  async sendTyping(conversationId: string) {
    const conversation = await this.getConversationBySid(conversationId);
    await conversation.typing();
  }
}
